"use client";

import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { useScroll, useTransform, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { COLORS } from "../../constants/COLORS";
import { ReactTyped } from "react-typed";

interface TimelineEntry {
  title: string;
  content: React.ReactNode;
}

export const Timeline = ({ data }: { data: TimelineEntry[] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewedItemsRef = useRef<Set<string>>(new Set());

  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, []);

  useEffect(() => {
    const items = document.querySelectorAll("[data-timeline-item]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const title = entry.target.getAttribute("data-timeline-title");

          if (!entry.isIntersecting || !title) return;

          if (viewedItemsRef.current.has(title)) return;

          viewedItemsRef.current.add(title);

          window.dataLayer?.push({
            event: "timeline_item_view",
            timeline_year: title,
          });

          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.5,
      },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  const textColor = useColorModeValue(COLORS.DARK_BLUE, COLORS.WHITE_SMOKE);

  return (
    <div
      className="w-full bg-transparent md:px-5"
      ref={containerRef}
      id="journey"
    >
      <div className="max-w-7xl mx-auto py-5 px-4 md:px-8 lg:px-10 text-center">
        <ReactTyped
          className="title"
          strings={["About My Journey"]}
          typeSpeed={90}
        />
        <Text className="subtitle">Key milestones and experiences</Text>
      </div>

      <div
        ref={ref}
        className="relative w-full max-w-7xl mx-auto pb-50"
        style={{ marginTop: "-5%" }}
      >
        {data.map((item, index) => (
          <div
            key={index}
            data-timeline-item
            data-timeline-title={item.title}
            className="flex justify-start pt-10 md:pt-40 md:gap-10"
          >
            <div className="sticky flex flex-col md:flex-row z-40 items-center top-40 self-start max-w-xs lg:max-w-sm md:w-full">
              <Box className="h-10 absolute left-0 md:left-3 w-10 rounded-full flex items-center justify-center">
                <Box
                  backgroundColor={textColor}
                  className="h-4 w-4 rounded-full border border-neutral-300 p-2"
                />
              </Box>

              <Text
                className="hidden md:block text-xl md:pl-20 md:text-5xl font-bold"
                color={textColor}
              >
                {item.title}
              </Text>
            </div>

            <div className="relative pl-12 pr-0 md:pl-4 md:pr-4 w-full">
              <Text
                className="md:hidden block text-2xl mb-4 text-left font-bold"
                color={textColor}
              >
                {item.title}
              </Text>

              {item.content}
            </div>
          </div>
        ))}

        <div
          style={{ height: height + "px" }}
          className="absolute md:left-8 left-5 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-neutral-200 dark:via-neutral-700 to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
        >
          <motion.div
            style={{ height: heightTransform, opacity: opacityTransform }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-black via-gray-600 to-gray-200 from-[0%] via-[50%] to-[100%] rounded-full"
          />
        </div>
      </div>
    </div>
  );
};
